/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { Analytics } from '@analytics';
import UsersIcon from '@assets/images/MemberIcon.svg';
import ResourcesIcon from '@assets/images/ResourcesIcon.svg';
import EmojiAvatar from '@components/EmojiAvatar';
import { CardViewType, GroupEntityType } from '@constants';
import { AccountCircle as AccountCircleIcon } from '@material-ui/icons';
import React from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import GroupResourceDetails from '../GroupResourceDetails';
import GroupUserDetails from '../GroupUserDetails';
import * as actions from '../TeamSettings/actions';
import messages from './messages';

// Tailwind class strings shared within this component
const iconClasses = 'flex w-10 h-10 justify-center items-center';
const avatarIconClasses = 'rounded-full shadow-md';

const GroupDetailsCard = ({
  group,
  isAGroup = true,
  type = GroupEntityType.resource,
  user,
  elements,
  showRemoveFromTeamDialog,
  isUserInGroup,
  fullSize = false,
  setCardView,
  teamDetails,
  clearTeamDetails,
  currentUser
}) => {
  const intl = useIntl();

  const mapToRemoveDialog = element => {
    if (type === GroupEntityType.resource) {
      return {
        icon: <EmojiAvatar code={element.emoji || ':credit_card:'} />,
        groupId: group._id,
        type: type,
        id: element._id,
        description: `${element.name} - ${intl.formatMessage(messages.removeResourceConfirmation)}`
      };
    }

    return {
      icon: element.profile.avatarUrl ? (
        <img
          className={`${iconClasses} ${avatarIconClasses}`}
          src={element.profile.avatarUrl}
          alt="User avatar"
        />
      ) : (
        <AccountCircleIcon
          className={`${iconClasses} ${avatarIconClasses} text-slate-300`}
        />
      ),
      groupId: group._id,
      type: type,
      id: element._id,
      description: `${element.profile.firstName} ${element.profile.lastName} - ${intl.formatMessage(
        messages.removeMemberConfirmation
      )}`
    };
  };

  const showElementDetails = element => {
    switch (type) {
      case GroupEntityType.resource: {
        return (
          <GroupResourceDetails
            key={element._id}
            name={element.name}
            emoji={element.emoji}
            shouldShowRemoveButton={isUserInGroup || currentUser.role === 'system_admin'}
            handleRemoveFromTeam={() => showRemoveFromTeamDialog(mapToRemoveDialog(element))}
          />
        );
      }
      case GroupEntityType.user: {
        return (
          <GroupUserDetails
            key={element._id}
            name={element.name}
            avatarUrl={element.profile.avatarUrl}
            firstName={element.profile.firstName}
            lastName={element.profile.lastName}
            isTheSameUser={element._id === user?.userId}
            shouldShowRemoveButton={
              (isUserInGroup || currentUser.role === 'system_admin') && element._id !== user.userId
            }
            handleRemoveFromTeam={() => showRemoveFromTeamDialog(mapToRemoveDialog(element))}
            splitPercentage={
              teamDetails?.userTipSplit.tipBreakdown.find(user => user.userId === element._id)
                ?.splitPercentage || 0
            }
            isUserInGroup={isUserInGroup}
          />
        );
      }
      case GroupEntityType.splitTip: {
        return (
          <GroupUserDetails
            key={element._id}
            name={element.name}
            avatarUrl={element.profile.avatarUrl}
            firstName={element.profile.firstName}
            lastName={element.profile.lastName}
            isTheSameUser={element._id === user?.userId}
            shouldShowRemoveButton={false}
          />
        );
      }
      default:
        return null;
    }
  };

  const handleTeamSettingsClick = () => {
    Analytics.track('Team Settings Clicked');

    // clear team details before showing the team settings, to fetch the latest data
    clearTeamDetails();
    setCardView(CardViewType.TeamSettings);
  };

  return (
    <>
      <div
        className={`p-4 shadow-sm border border-solid border-slate-200 rounded-3xl bg-slate-50 flex flex-col justify-start flex-[1_0_0%] m-auto mb-4 ${
          fullSize ? 'sm:max-w-full' : 'sm:max-w-[400px]'
        }`}>
        <div className="flex flex-row mb-2.5 justify-between w-full">
          <div className="flex flex-row">
            <div className={iconClasses}>
              <img
                src={type === GroupEntityType.resource ? ResourcesIcon : UsersIcon}
                alt="resources icon"
              />
            </div>
            <div className="flex flex-col pl-2">
              <div className="text-base font-bold text-slate-800">
                {type === GroupEntityType.resource
                  ? intl.formatMessage(messages.resources)
                  : intl.formatMessage(messages.members)}
              </div>
              <div className="text-xs font-semibold text-slate-400">
                {isAGroup
                  ? intl.formatMessage(messages.subText)
                  : intl.formatMessage(messages.noTeam)}
              </div>
            </div>
          </div>

          {type === GroupEntityType.user && isUserInGroup && (
            <div
              onClick={handleTeamSettingsClick}
              className="text-indigo-600 hover:text-indigo-700 text-[11px] font-semibold cursor-pointer transition-colors">
              {intl.formatMessage(messages.teamSettings)}
            </div>
          )}
        </div>
        {elements && elements.map(showElementDetails)}
      </div>
    </>
  );
};

const mapStateToProps = state => ({
  teamDetails: state.teamSettings?.teamDetails,
  currentUser: state.user?.user
});

const mapDispatchToProps = dispatch => ({
  clearTeamDetails: () => dispatch(actions.clearTeamDetails())
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(GroupDetailsCard);
